import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router';
import {
  getCoreRowModel,
  getFilteredRowModel,
  getSortedRowModel,
  useReactTable,
} from '@tanstack/react-table';
import { useAdminApiQuery } from '@/hooks/use-admin-api-query';
import { qk } from '@/lib/query/keys';
import { PageContainer } from '@/components/page-container';
import { Input } from '@/components/ui/input';
import { DataTable } from '@/components/super-admin/data-table';
import {
  channelColumns,
  channelGlobalFilter,
  type ChannelRow,
} from './channels-columns';

export function SuperAdminChannelsPage() {
  const navigate = useNavigate();
  const [search, setSearch] = useState('');
  const { data, isLoading } = useAdminApiQuery<ChannelRow[]>(
    qk.superAdmin('channels'),
    '/super-admin/channels',
  );

  // Must be memoized: a fresh array identity each render remounts every cell.
  const rows = useMemo(() => data ?? [], [data]);

  const table = useReactTable({
    data: rows,
    columns: channelColumns,
    getRowId: (row) => row.id,
    defaultColumn: { enableSorting: false },
    enableMultiSort: false,
    sortDescFirst: false,
    state: { globalFilter: search },
    onGlobalFilterChange: setSearch,
    globalFilterFn: channelGlobalFilter,
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getSortedRowModel: getSortedRowModel(),
  });

  return (
    <PageContainer variant="fluid" className="space-y-6">
      <Input
        aria-label="Search channels"
        placeholder="Search channels by name, phone, IG handle, WABA ID, workspace, or id..."
        value={search}
        onChange={(e) => table.setGlobalFilter(e.target.value)}
        className="max-w-md"
      />
      <div className="rounded-md border">
        <DataTable
          table={table}
          isLoading={isLoading}
          emptyState="No channels found."
          onRowActivate={(row) =>
            navigate(
              `/super-admin/workspaces/${row.original.workspace.id}?channel=${row.original.id}`,
            )
          }
        />
      </div>
    </PageContainer>
  );
}
